import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import { FastField, Field } from 'formik';
import { required } from '../../../../utils/validators';
import VisitWhenPicker from './VisitWhenPicker';
import ProviderModeSection from './ProviderModeSection';
import AddNotePicker from './AddNotePicker';
import RecurrenceSection from './RecurrenceSection';
import { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from '../ImagesField';
import { FormValueImage } from '../ImagesField/types';
import { visitTypeMessages } from '../../../../utils/sharedMessages';
import { PrimaryRecordVisit, VisitDuration } from '../../../../primary-record-types/MedicalDataTypes';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import { getProviderPropsInitialValues, getProviderSubmissionValues } from '../ProviderFields';
import useRecordEvent from '../../../../hooks/useRecordEvent';
import { Form, SplitFormRow } from '../../../v2BaseComponents/Form';
import { ContentStack } from '../../../v2BaseComponents/Stack';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { ProviderSupportList } from '../RenderWithProviders';

const intl = getIntl('VisitForm', {
    save: 'Save',
    visitType: 'Visit Type',
    visitTypeOtherLabel: 'Other Visit Type',
    reason: 'Reason',
    notes: 'Description',
    provider: 'Provider',
    providerName: 'Provider Name',
    address: 'Address',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    selectCareTeamProvider: 'Select Care Team Provider',
});

export type VisitFormOwnProps = {
    providers: ProviderSupportList;
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordVisit;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: VisitFormSubmitData) => void;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    onCancel: () => void;
    saveText?: string;
    /** Org flows only: start the provider section in Organization mode. */
    showOrganizationUserSelect?: boolean;
    /** Open the When picker immediately (e.g. arriving from the Share Visit action). */
    autoOpenWhenPicker?: boolean;
};

type VisitResourceFormValue = Omit<PrimaryRecordVisit, 'duration'> & { duration: string | null } & {
    selectExistingProvider: boolean;
};

type FormValue = {
    resource: VisitResourceFormValue;
    images: FormValueImage[];
    sourceNoteId: string | null;
};

export const getDefaultFormValue = (selectExistingProvider: boolean, startDateTime?: string): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'visit',
        visitType: null,
        visitTypeOtherLabel: null,
        recurrenceGroupId: null,
        recurrence: null,
        reason: null,
        startDateTime,
        duration: null,
        endDateTime: undefined,
        notes: '',
        providerCareTeamMemberId: null,
        providerName: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zipCode: null,
        selectExistingProvider,
    },
    images: [],
    sourceNoteId: null,
});

export type VisitFormProps = VisitFormOwnProps;

export type VisitFormSubmitData =
    APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordVisit> & {
        /** Org note to copy onto the created visit(s) — create flows only. */
        sourceNoteId?: string;
    };

export const handleVisitFormSubmit = (values: FormValue, providers: ProviderSupportList): VisitFormSubmitData => {
    const { duration, ...resource } = values.resource;
    const numericDuration: VisitDuration | null = duration ? (parseInt(duration) as VisitDuration) : null;
    let endDateTime = resource.endDateTime;
    if (numericDuration && resource.startDateTime) {
        const endDateValue = new Date(resource.startDateTime);
        endDateValue.setMinutes(endDateValue.getMinutes() + numericDuration);
        endDateTime = new Date(endDateValue).toISOString();
    }

    return {
        resourceUpdate: {
            ...getProviderSubmissionValues(values.resource, providers, false),
            startDateTime: values.resource.startDateTime || undefined,
            duration: numericDuration,
            endDateTime,
            // The custom label only makes sense alongside the 'other' type — drop any
            // stale value left behind by switching the type after filling it in.
            visitTypeOtherLabel: values.resource.visitType === 'other' ? values.resource.visitTypeOtherLabel : null,
        },
        imageUpdates: formValueImagesToResourceImageUpdates(values.images),
        // The note picker unmounts when recurrence is enabled, but Formik keeps the stale
        // value - a recurring series never submits a note.
        sourceNoteId: values.resource.recurrence ? undefined : values.sourceNoteId || undefined,
    };
};

export const VisitFormFields: React.FC<{
    isDisabled: boolean;
    isLoading: boolean;
    values: FormValue;
    providers: ProviderSupportList;
    resourceId?: string;
    profileId: string;
    setFieldValue: (field: string, value: any) => void;
    showOrganizationUserSelect?: boolean;
    autoOpenWhenPicker?: boolean;
    /** Create flows only — notes can't be attached retroactively through this form. */
    showNotePicker?: boolean;
    /** Create flows only — a recurring series can't be introduced on an existing visit. */
    showRecurrenceControls?: boolean;
}> = ({
    isDisabled,
    isLoading,
    values,
    providers,
    setFieldValue,
    showOrganizationUserSelect,
    autoOpenWhenPicker,
    showNotePicker,
    showRecurrenceControls,
}) => (
    <ContentStack>
        <SplitFormRow>
            <VisitWhenPicker
                startDateTime={values.resource.startDateTime}
                endDateTime={values.resource.endDateTime}
                duration={values.resource.duration}
                isDisabled={isDisabled || isLoading}
                setFieldValue={setFieldValue}
                autoOpen={autoOpenWhenPicker}
            />
        </SplitFormRow>
        {showRecurrenceControls ? (
            <RecurrenceSection values={values} isDisabled={isDisabled || isLoading} setFieldValue={setFieldValue} />
        ) : null}
        <SplitFormRow>
            <FastField
                name="resource.visitType"
                isRequired
                title={intl('visitType')}
                validate={required}
                isDisabled={isDisabled || isLoading}
                component={FormikInputs.Select}
                selectOptions={(['routine', 'walk-in', 'checkup', 'followup', 'emergency', 'other'] as const).map(
                    (_) => ({
                        label: visitTypeMessages(_),
                        value: _,
                    }),
                )}
            />
        </SplitFormRow>
        {values.resource.visitType === 'other' ? (
            <SplitFormRow>
                <Field
                    name="resource.visitTypeOtherLabel"
                    isRequired
                    title={intl('visitTypeOtherLabel')}
                    validate={required}
                    component={FormikInputs.Input}
                    isDisabled={isDisabled || isLoading}
                />
            </SplitFormRow>
        ) : null}
        <SplitFormRow>
            <FastField
                name="resource.reason"
                title={intl('reason')}
                component={FormikInputs.Input}
                isDisabled={isDisabled || isLoading}
            />
        </SplitFormRow>
        <SplitFormRow>
            <FastField
                name="resource.notes"
                title={intl('notes')}
                component={FormikInputs.TextArea}
                isDisabled={isDisabled || isLoading}
            />
        </SplitFormRow>
        {/* Note comes before the provider/location section so the fields below it are all location. */}
        {/* Bulk create (recurring series) rejects note attachment - hide the picker for series. */}
        {showNotePicker && !values.resource.recurrence ? <AddNotePicker isDisabled={isDisabled || isLoading} /> : null}
        <ProviderModeSection
            values={values}
            providers={providers}
            isDisabled={isDisabled}
            isLoading={isLoading}
            setFieldValue={setFieldValue}
            defaultMode={showOrganizationUserSelect ? 'organization' : undefined}
        />
    </ContentStack>
);

const VisitForm: React.FC<VisitFormProps> = ({
    providers,
    isLoading,
    onSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    onCancel,
    saveText,
    showOrganizationUserSelect,
    autoOpenWhenPicker,
}) => {
    // Org flows open on Organization even when the client has a care team — Provider is
    // offered as an option, not the starting mode (selectExistingProvider would win over
    // ProviderModeSection's defaultMode otherwise).
    const defaultSelectExistingProvider = showOrganizationUserSelect ? false : !!providers.length;
    const initialValues = useMemo<FormValue>(() => {
        if (defaultResource && defaultImages) {
            return {
                ...(getProviderPropsInitialValues(
                    providers,
                    {
                        resource: {
                            ...defaultResource,
                            selectExistingProvider: !!defaultResource.providerCareTeamMemberId,
                            ...({
                                duration: defaultResource.duration ? String(defaultResource.duration) : null,
                            } as any),
                        },
                    },
                    getDefaultFormValue(defaultSelectExistingProvider, startDateTime),
                ) as Pick<FormValue, 'resource'>),
                images: resourceImagesToFormValueImages(defaultImages),
                sourceNoteId: null,
            };
        } else {
            return getDefaultFormValue(defaultSelectExistingProvider, startDateTime);
        }
    }, [defaultImages, defaultResource, providers, startDateTime, defaultSelectExistingProvider]);
    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            saveText={saveText}
            onSubmit={(values, helpers) => {
                onSubmit(handleVisitFormSubmit(values, providers));
                handleFormikSubmit(values, helpers);
                recordEvent(!!defaultResource ? 'editVisit' : 'createVisit');
            }}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
        >
            {({ values, setFieldValue }) => {
                return (
                    <VisitFormFields
                        isDisabled={isDisabled}
                        isLoading={isLoading}
                        values={values}
                        providers={providers}
                        resourceId={resourceId}
                        profileId={profileId}
                        setFieldValue={setFieldValue}
                        showOrganizationUserSelect={showOrganizationUserSelect}
                        autoOpenWhenPicker={autoOpenWhenPicker}
                        showNotePicker={!defaultResource}
                        showRecurrenceControls={!defaultResource}
                    />
                );
            }}
        </Form>
    );
};

export default React.memo(VisitForm);
